/**
 * Pool shaping — pure helpers shared by MCP one-shot select and any day
 * suggest path that builds a role pool before ranking.
 *
 * These never talk to the database. Callers load candidates first.
 */

import { fold } from './brief';

const titleStem = (title: string): string => {
  const tokens = fold(title)
    .split(/[^a-z0-9]+/)
    .filter((token) => token.length > 2);
  return tokens.slice(0, 2).join(' ') || fold(title);
};

export type PoolProduct = {
  readonly id: string;
  readonly title: string;
  readonly subcategory?: string | null;
};

/** At most one product per subcategory, else per folded title stem. */
export const dedupeBySubcategory = <T extends PoolProduct>(
  rows: readonly T[]
): T[] => {
  const seen = new Set<string>();
  const kept: T[] = [];
  for (const row of rows) {
    const key = row.subcategory?.trim()
      ? `sub:${fold(row.subcategory)}`
      : `stem:${titleStem(row.title)}`;
    if (seen.has(key)) continue;
    seen.add(key);
    kept.push(row);
  }
  return kept;
};

/** A stated pax band, or null when the title does not state one. */
export const paxBand = (title: string): { min: number; max: number } | null => {
  const range = title.match(/(\d+)\s*[-–]\s*(\d+)\s*pax/i);
  if (range) return { min: Number(range[1]), max: Number(range[2]) };
  const plus = title.match(/(\d+)\s*\+\s*pax/i);
  if (plus) return { min: Number(plus[1]), max: Number.POSITIVE_INFINITY };
  return null;
};

/**
 * Drop vehicles whose stated band excludes the party. A title with no band
 * stays. An empty result keeps the page and says so — never invent a vehicle.
 */
export const transfersForParty = <T extends PoolProduct>(
  rows: readonly T[],
  pax: number
): { rows: T[]; note?: string } => {
  const fitting = rows.filter((row) => {
    const band = paxBand(row.title);
    return band === null || (pax >= band.min && pax <= band.max);
  });
  if (rows.length === 0) return { rows: [] };
  if (fitting.length === 0) {
    return {
      rows: [...rows],
      note: `No transfer stated a pax band covering ${pax}. The full transfer page was kept.`,
    };
  }
  if (fitting.length === rows.length) return { rows: fitting };
  return {
    rows: fitting,
    note: `Transfer page narrowed to vehicles whose stated pax band covers ${pax}.`,
  };
};

/** Higher net trips first. Ties keep the incoming order. */
export const orderByNetTrips = <T extends { readonly id: string }>(
  rows: readonly T[],
  netByProduct: ReadonlyMap<string, number>
): T[] =>
  rows
    .map((row, index) => ({
      row,
      index,
      net: netByProduct.get(row.id) ?? Number.NEGATIVE_INFINITY,
    }))
    .sort((left, right) => right.net - left.net || left.index - right.index)
    .map((entry) => entry.row);

/** Co-occurrence is a secondary key. Missing pairs keep the incoming order. */
export const orderByCoTrips = <T extends { readonly id: string }>(
  rows: readonly T[],
  coTripsByProduct: ReadonlyMap<string, number>
): T[] =>
  rows
    .map((row, index) => ({
      row,
      index,
      co: coTripsByProduct.get(row.id) ?? 0,
    }))
    .sort((left, right) => right.co - left.co || left.index - right.index)
    .map((entry) => entry.row);

/**
 * Arrival and departure are not two halves of a sold-history sort.
 * Destination in the region (or hotel) → inbound; hub/airport → outbound.
 * A vehicle that does not name the region is dropped when any vehicle does.
 */
export const splitTransferDirections = <T extends PoolProduct>(
  rows: readonly T[],
  regionToken: string,
  hub: string | undefined
): {
  inbound: T[];
  outbound: T[];
  note?: string;
} => {
  const token = fold(regionToken);
  const hubWord = hub
    ? fold(hub)
        .split(/[^a-z]+/)
        .find((word) => word.length >= 4)
    : undefined;
  const local = token
    ? rows.filter((row) => fold(row.title).includes(token))
    : [...rows];
  const page = local.length > 0 ? local : [...rows];
  const inbound: T[] = [];
  const outbound: T[] = [];
  const either: T[] = [];
  for (const row of page) {
    const text = fold(row.title);
    const toAt = text.lastIndexOf(' to ');
    const dest = toAt >= 0 ? text.slice(toAt + 4) : '';
    const away =
      dest.length > 0 &&
      (/airport/.test(dest) || (hubWord ? dest.includes(hubWord) : false));
    const toward =
      dest.length > 0 && (dest.includes(token) || /\bhotel\b/.test(dest));
    if (toward && !away) inbound.push(row);
    else if (away && !toward) outbound.push(row);
    else either.push(row);
  }
  const notes = [
    local.length > 0 && local.length < rows.length
      ? `Transfer page kept vehicles naming ${regionToken}.`
      : undefined,
    inbound.length === 0 && either.length === 0
      ? `No arrival transfer names ${regionToken} and fits the party. The role was left unfilled rather than booking the departure car.`
      : undefined,
  ].filter((line): line is string => line !== undefined);
  return {
    inbound: inbound.length > 0 ? inbound : either,
    outbound: outbound.length > 0 ? outbound : either,
    ...(notes.length > 0 ? { note: notes.join(' ') } : {}),
  };
};

/** Split a ranked list into N disjoint slices for multi-slot Jev questions. */
export const splitDisjoint = <T>(rows: readonly T[], parts: number): T[][] => {
  if (parts <= 0) return [];
  const slices: T[][] = Array.from({ length: parts }, () => []);
  rows.forEach((row, index) => {
    const bucket = Math.min(
      parts - 1,
      Math.floor((index * parts) / Math.max(rows.length, 1))
    );
    slices[bucket]?.push(row);
  });
  return slices.filter((slice) => slice.length > 0);
};

export const firstHit = <T extends { readonly id: string }>(
  rows: readonly T[],
  used: ReadonlySet<string>
): T | undefined => rows.find((row) => !used.has(row.id));
